import { computed, reactive } from "vue";
import { showSnackbar } from "@/UIHelper";

export type FrontendJobStatusState =
  | "queued"
  | "running"
  | "success"
  | "error"
  | "cancelled"
  | "unknown";

export interface JobStatusUpdate {
  status: string;
  progress?: number | null;
  jobId?: string | number | null;
  ids?: Array<string | number | undefined | null>;
  title?: string | null;
  message?: string | null;
}

export interface JobStatusEntry {
  key: string;
  status: FrontendJobStatusState;
  progress: number | null;
  title: string;
  message: string;
  ids: Array<string | number>;
  createdAt: number;
  updatedAt: number;
}

const TERMINAL_STATES: FrontendJobStatusState[] = ["success", "error", "cancelled"];
const AUTO_DISMISS_MS = 4000;

const state = reactive<{ items: JobStatusEntry[] }>({
  items: [],
});

const dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

function normalizeStatus(status: string): FrontendJobStatusState {
  const value = (status ?? "").trim().toLowerCase();
  switch (value) {
    case "queued":
    case "pending":
    case "wartet":
      return "queued";
    case "running":
    case "started":
    case "processing":
    case "laeuft":
      return "running";
    case "success":
    case "done":
    case "completed":
    case "complete":
    case "ok":
    case "fertig":
    case "beendet":
    case "abgeschlossen":
      return "success";
    case "error":
    case "failed":
    case "fehler":
      return "error";
    case "cancelled":
    case "canceled":
    case "abgebrochen":
      return "cancelled";
    default:
      return "unknown";
  }
}

function normalizeIds(ids?: Array<string | number | undefined | null>) {
  return (ids ?? []).filter(
    (value): value is string | number => value !== undefined && value !== null && value !== ""
  );
}

function normalizeText(value: unknown) {
  if (value === undefined || value === null) {
    return "";
  }
  return String(value).trim();
}

function normalizeTitle(value: unknown) {
  const title = normalizeText(value);
  if (!title) {
    return "";
  }
  const compactTitle = title.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (compactTitle.includes("autombelegerkennung")) {
    return "Belegerkennung";
  }
  return title;
}

function normalizeProgress(progress?: number | null, status?: FrontendJobStatusState) {
  if (progress === undefined || progress === null || Number.isNaN(progress)) {
    return status === "success" ? 100 : null;
  }
  if (status === "queued" && progress <= 0) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(progress)));
}

function buildJobKey(jobId: string | number | null | undefined, ids: Array<string | number>) {
  if (jobId !== undefined && jobId !== null && jobId !== "") {
    return String(jobId);
  }
  if (ids.length > 0) {
    return ids.map((value) => String(value)).join(":");
  }
  return "global-job-status";
}

function normalizeIdsForLookup(ids: Array<string | number>) {
  return [...ids]
    .map((value) => String(value))
    .sort((left, right) => left.localeCompare(right));
}

function haveSameIds(left: Array<string | number>, right: Array<string | number>) {
  if (left.length !== right.length) {
    return false;
  }
  const normalizedLeft = normalizeIdsForLookup(left);
  const normalizedRight = normalizeIdsForLookup(right);
  return normalizedLeft.every((value, index) => value === normalizedRight[index]);
}

function findExistingEntry(jobId: string | number | null | undefined, ids: Array<string | number>) {
  const directKey = buildJobKey(jobId, ids);
  const directMatch = state.items.find((entry) => entry.key === directKey);
  if (directMatch) {
    return directMatch;
  }
  if (jobId !== undefined && jobId !== null && jobId !== "") {
    return undefined;
  }
  if (ids.length === 0) {
    return undefined;
  }
  return state.items.find((entry) => haveSameIds(entry.ids, ids));
}

function statusLabel(status: FrontendJobStatusState) {
  switch (status) {
    case "queued":
      return "Wartet";
    case "running":
      return "Läuft";
    case "success":
      return "Abgeschlossen";
    case "error":
      return "Fehler";
    case "cancelled":
      return "Abgebrochen";
    default:
      return "Unbekannt";
  }
}

function defaultTitle(ids: Array<string | number>) {
  return "Hintergrundjob";
}

function defaultMessage(status: FrontendJobStatusState, ids: Array<string | number>) {
  if (status === "unknown") {
    return "Status wird ermittelt";
  }
  return statusLabel(status);
}

function isMissingStatusMessage(message: string) {
  const value = normalizeText(message).toLowerCase();
  if (!value) {
    return false;
  }
  return (
    value.includes("kein status gefunden")
    || value.includes("status nicht gefunden")
    || value.includes("no status found")
    || value.includes("status not found")
    || value.includes("not found")
  );
}

function clearDismissTimer(key: string) {
  const timer = dismissTimers.get(key);
  if (timer) {
    clearTimeout(timer);
    dismissTimers.delete(key);
  }
}

function notifyTerminalState(entry: JobStatusEntry) {
  if (entry.status === "success") {
    showSnackbar({
      text: `${entry.title} abgeschlossen`,
      color: "success",
      duration: 2500,
    });
    return;
  }
  if (entry.status === "error") {
    showSnackbar({
      text: `${entry.title}: ${entry.message || "Fehler"}`,
      color: "error",
      duration: 5000,
    });
    return;
  }
  if (entry.status === "cancelled") {
    showSnackbar({
      text: `${entry.title} wurde abgebrochen`,
      color: "warning",
      duration: 3500,
    });
  }
}

export function dismissJobStatus(key: string) {
  clearDismissTimer(key);
  state.items = state.items.filter((entry) => entry.key !== key);
}

export function applyJobStatusUpdate(update: JobStatusUpdate) {
  const ids = normalizeIds(update.ids);
  let status = normalizeStatus(update.status);
  const existing = findExistingEntry(update.jobId, ids);
  const key = existing?.key ?? buildJobKey(update.jobId, ids);
  const incomingMessage = normalizeText(update.message);
  let progress = normalizeProgress(update.progress, status);
  // Falls Backends nur Fortschritt liefern, aber keinen finalen Status, Job trotzdem sauber abschliessen.
  if ((status === "running" || status === "queued" || status === "unknown") && progress === 100) {
    status = "success";
    progress = 100;
  }
  // Einige Backends liefern nach Abschluss keinen Status mehr ("Kein Status gefunden").
  // In dem Fall den Job als erledigt markieren, damit er automatisch geschlossen wird.
  if (status === "unknown" && isMissingStatusMessage(incomingMessage)) {
    status = existing && TERMINAL_STATES.includes(existing.status) ? existing.status : "success";
    progress = 100;
  }
  const now = Date.now();
  const nextEntry: JobStatusEntry = {
    key,
    status,
    progress,
    title: normalizeTitle(update.title) || existing?.title || defaultTitle(ids),
    message: (
      status === "success" && isMissingStatusMessage(incomingMessage)
        ? "Abgeschlossen"
        : incomingMessage || existing?.message || defaultMessage(status, ids)
    ),
    ids,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
  };

  if (existing) {
    Object.assign(existing, nextEntry);
  } else {
    state.items.unshift(nextEntry);
  }

  const entry = existing ?? nextEntry;
  clearDismissTimer(key);

  if (TERMINAL_STATES.includes(status)) {
    if (!existing || existing.status !== status) {
      notifyTerminalState(entry);
    }
    dismissTimers.set(
      key,
      setTimeout(() => {
        dismissJobStatus(key);
      }, AUTO_DISMISS_MS)
    );
  }

  state.items.sort((left, right) => right.updatedAt - left.updatedAt);
  return entry;
}

export function setJobStatus(
  status: string,
  progress: number,
  ...ids: Array<string | number | undefined | null>
) {
  return applyJobStatusUpdate({ status, progress, ids });
}

export function getJobStatusColor(status: FrontendJobStatusState) {
  switch (status) {
    case "success":
      return "success";
    case "error":
      return "error";
    case "cancelled":
      return "warning";
    default:
      return "primary";
  }
}

export function getJobStatusLabel(status: FrontendJobStatusState) {
  return statusLabel(status);
}

export function isTerminalJobStatus(status: FrontendJobStatusState) {
  return TERMINAL_STATES.includes(status);
}

export const jobStatusEntries = computed(() => [...state.items]);